import { useCallback, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import pagedGet from '../services/pagedGet'

type UseDebouncedPagedSearchOptions = {
  /** How many rows to ask the backend for. */
  pageSize?: number
  /** How long to wait after the last keystroke before calling the API. */
  delayMs?: number
  /** When true, an empty query still loads the first page (useful for autocomplete). */
  fetchOnEmpty?: boolean
}

/**
 * Searches a paged backend resource as the user types, without firing a request
 * on every keystroke.
 *
 * Flow:
 * 1. `setInputValue` updates the field immediately (the UI stays responsive).
 * 2. A timer waits `delayMs`. Each new keystroke resets that timer (debounce).
 * 3. When the user pauses, one GET is sent (`/recurso/search?pesquisa=`).
 * 4. If they type again, the in-flight request is cancelled so stale results
 *    cannot overwrite a newer search.
 *
 * `searchImmediately` skips the wait (e.g. barcode scanner pressing Enter).
 */
const useDebouncedPagedSearch = <T,>(
  resource: string,
  {
    pageSize = 20,
    delayMs = 400,
    fetchOnEmpty = false,
  }: UseDebouncedPagedSearchOptions = {}
) => {
  const [inputValue, setInputValue] = useState('')
  const [items, setItems] = useState<T[]>([])
  const [loading, setLoading] = useState(false)

  // Keep the latest query in a ref so `searchImmediately` does not close over a stale value.
  const inputValueRef = useRef(inputValue)
  // Monotonic id: only the latest request is allowed to write to `items`.
  const requestSeq = useRef(0)
  // Cancels the axios call that is currently in flight, if any.
  const cancelCurrent = useRef<(() => void) | null>(null)
  // Handle of the debounce timer that has not fired yet.
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  inputValueRef.current = inputValue

  const clearScheduled = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }

  const fetchItems = useCallback(async (query: string): Promise<T[]> => {
    const q = (query || '').trim()

    // Empty input: drop results and do not hit the API (unless fetchOnEmpty).
    if (!q && !fetchOnEmpty) {
      requestSeq.current += 1
      cancelCurrent.current?.()
      setItems([])
      setLoading(false)
      return []
    }

    // Abort the previous call so the backend is not kept busy with outdated queries.
    cancelCurrent.current?.()
    const seq = ++requestSeq.current
    const source = axios.CancelToken.source()
    cancelCurrent.current = () => source.cancel()
    setLoading(true)

    try {
      const result = await pagedGet<T>(resource, {
        page: 1,
        pageSize,
        pesquisa: q || undefined,
        cancelToken: source.token,
        // Search-as-you-type should not flash the global loading overlay.
        params: { avoidLoadingScreen: true },
      })

      // A newer search started while this one was in flight: ignore these results.
      if (seq !== requestSeq.current) {
        return []
      }

      setItems(result.items)
      return result.items
    } catch (error) {
      if (axios.isCancel(error)) {
        return []
      }

      if (seq === requestSeq.current) {
        setItems([])
      }

      return []
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false)
      }
    }
  }, [resource, pageSize, fetchOnEmpty])

  // Debounce: wait until typing stops, then fetch. Clearing the field runs immediately.
  useEffect(() => {
    clearScheduled()
    cancelCurrent.current?.()

    const q = inputValue.trim()
    const delay = !q ? 0 : delayMs

    timeoutRef.current = setTimeout(() => {
      fetchItems(inputValue)
    }, delay)

    return () => {
      clearScheduled()
    }
  }, [inputValue, delayMs, fetchItems])

  // Unmount: stop the timer and abort any request still running.
  useEffect(() => {
    return () => {
      clearScheduled()
      cancelCurrent.current?.()
    }
  }, [])

  // Used when the caller cannot wait for the debounce (Enter / barcode).
  const searchImmediately = useCallback((query?: string) => {
    clearScheduled()
    return fetchItems(query ?? inputValueRef.current)
  }, [fetchItems])

  return {
    inputValue,
    setInputValue,
    items,
    setItems,
    loading,
    searchImmediately,
  }
}

export default useDebouncedPagedSearch
